import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import { formatCurrency } from '@/libs/antd/formater';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { FleetSearch } from '@/libs/fleet-api/fetcher';
import {
	MonitoringPlafondCustomerDetailParam,
	useMonitoringPlafondCustomerDetail,
} from '@/libs/fleet-api/services/monitoring';
import { ArrowRightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	Progress,
	Row,
	Select,
	Table,
	Typography,
} from 'antd';
import dayjs from 'dayjs';
import { FC, Fragment, useState } from 'react';

const DEFAULT_PARAM: FleetSearch = {
	by: '',
	value: '',
};

type MonitoringPlafondPageProps = MonitoringPlafondCustomerDetailParam & {
	onBack: () => void;
};
const MonitoringPlafondPage: FC<MonitoringPlafondPageProps> = ({
	onBack,
	customerId,
	search,
}) => {
	const { navigate } = useApp();
	const { openCustomerDetail, openCustomerGroupDetail } = useModals();
	const { form, fieldName } = useForm<FleetSearch>();
	const [param, setParam] = useState<FleetSearch>({
		...DEFAULT_PARAM,
		...search,
	});

	const customerDetailQuery = useMonitoringPlafondCustomerDetail({
		customerId,
		search: param,
	});

	const {
		customerName,
		bmppGroupId,
		bmppGroupName,
		// mklNo,
		// plafonType,
		// spfNo,
		searchByOptions = [],
		plafons = [],
		// financingLimitType,
		financingLimitInfo,
	} = customerDetailQuery?.data?.data ?? {};

	const renderProgress = ({
		name,
		limit,
		total,
	}: {
		name: string;
		limit: number;
		total: number;
	}) => {
		return (
			<div style={{ marginBlock: 40 }}>
				<Row>
					<Col flex="auto">
						<strong>{name}</strong>
					</Col>
					<Col flex="none">{formatCurrency(total)}</Col>
				</Row>
				<Progress
					percent={((total - limit) / total) * 100}
					showInfo={false}
				/>
				<Row>
					<Col flex="auto">Sisa Limit:</Col>
					<Col flex="none">{formatCurrency(limit)}</Col>
				</Row>
			</div>
		);
	};

	return (
		<Fragment>
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 20,
				}}
			>
				{typeof onBack === 'function' && (
					<div>
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={() => onBack?.()}
						>
							<ArrowLeftOutlined />
						</button>
					</div>
				)}
				<div style={{ flex: 1 }}>
					<FleetTitle style={{ marginBottom: 20 }}>
						Monitoring Plafon
					</FleetTitle>
				</div>
			</div>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				layout="horizontal"
				colon={false}
				labelAlign="left"
				labelCol={{ style: { width: 160 } }}
				style={{ marginBlock: 20 }}
				onFinish={setParam}
			>
				<Row
					gutter={20}
					wrap={false}
				>
					<Col flex="none">
						<Form.Item
							label="Search by"
							name={fieldName('by')}
						>
							<Select
								allowClear
								style={{ width: 200 }}
								options={searchByOptions.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
								onSelect={() => {
									form.resetFields([fieldName('value')]);
								}}
								onClear={() => {
									form.resetFields([fieldName('by'), fieldName('value')]);
								}}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">
						<Form.Item
							name={fieldName('value')}
							noStyle
						>
							<Input />
						</Form.Item>
					</Col>
					<Col flex="none">
						<Button
							block
							style={{ width: 200 }}
							type="primary"
							htmlType="submit"
							disabled={customerDetailQuery.isFetching}
							loading={customerDetailQuery.isLoading}
						>
							SEARCH
						</Button>
					</Col>
				</Row>
				<Divider />
			</Form>

			<Typography.Title level={3}>Main Information</Typography.Title>
			<DataRow
				loading={customerDetailQuery.isFetching}
				items={[
					[
						[
							'Nama Customer',
							customerId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(customerId);
									}}
								>
									{customerName}
								</a>
							) : (
								customerName ?? '-'
							),
						],
						[
							'Group Customer BMPP',
							bmppGroupId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(bmppGroupId);
									}}
								>
									{bmppGroupName}
								</a>
							) : (
								bmppGroupName ?? '-'
							),
						],
					],
					// [
					// 	['No.SPF ', spfNo ?? '-'],
					// 	['No.MKL ', mklNo ?? '-'],
					// ],
					// [
					// 	['Sifat Limit', financingLimitType?.replaceAll(';', ', ') ?? '-'],
					// 	['Jenis Limit', plafonType?.replaceAll(';', ', ') ?? '-'],
					// ],
				]}
			/>

			<Divider />
			<Typography.Title level={3}>Informasi Limit Pembiayaan</Typography.Title>
			<Row gutter={40}>
				<Col span={12}>
					<Typography.Title level={3}>Non-Revolving</Typography.Title>
					{renderProgress({
						name: 'Total Limit Existing NJF',
						total: financingLimitInfo?.nonRevolvingNjfTotalLimitAmount ?? 0,
						limit: financingLimitInfo?.nonRevolvingNjfLimitExistingAmount ?? 0,
					})}
					{renderProgress({
						name: 'Total Limit Existing JF',
						total: financingLimitInfo?.nonRevolvingJfTotalLimitAmount ?? 0,
						limit: financingLimitInfo?.nonRevolvingJfLimitExistingAmount ?? 0,
					})}
				</Col>
				<Col span={12}>
					<Typography.Title level={3}>Revolving</Typography.Title>
					{renderProgress({
						name: 'Total Limit Existing NJF',
						total: financingLimitInfo?.revolvingNjfTotalLimitAmount ?? 0,
						limit: financingLimitInfo?.revolvingNjfLimitExistingAmount ?? 0,
					})}
					{renderProgress({
						name: 'Total Limit Existing JF',
						total: financingLimitInfo?.revolvingJfTotalLimitAmount ?? 0,
						limit: financingLimitInfo?.revolvingJfLimitExistingAmount ?? 0,
					})}
				</Col>
			</Row>

			<Divider />
			<Typography.Title level={3}>List Informasi Plafon</Typography.Title>

			<Table
				style={{ marginBlock: 20 }}
				bordered
				scroll={{ x: 'max-content' }}
				loading={customerDetailQuery.isFetching}
				dataSource={plafons?.map((record, key) => {
					return {
						...record,
						key,
					};
				})}
				pagination={false}
				columns={[
					{
						title: 'No',
						align: 'center',
						fixed: 'left',
						width: 1,
						render: (_, __, index) => index + 1,
					},
					{
						title: 'No MKL',
						render: (_, record) => record.mkLNo ?? '-',
					},
					{
						title: 'No. SPF',
						render: (_, record) => {
							if (record.spfId && record.resolutionId) {
								return (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											navigate('SPFPage', {
												spfId: record.spfId,
												napResolutionId: record.resolutionId ?? 0,
												onBack: () =>
													navigate('MonitoringPlafondPage', {
														customerId,
														onBack,
														search: param,
													}),
												viewOnly: true,
											});
										}}
									>
										{record.spfNo ?? '-'}
									</a>
								);
							}
							return record.spfNo ?? '-';
						},
					},
					{
						title: 'Limit Pembiayaan',
						render: (_, record) =>
							record.financingLimitAmount
								? formatCurrency(record.financingLimitAmount)
								: '-',
					},
					{
						title: 'Sifat Limit',
						render: (_, record) =>
							record.financingLimitType?.replaceAll(';', ', ') ?? '-',
					},
					{
						title: 'Jenis Limit',
						render: (_, record) =>
							record.plafonType?.replaceAll(';', ', ') ?? '-',
					},
					{
						title: 'Expired',
						render: (_, record) =>
							record.expiredDate
								? dayjs(record.expiredDate).format('DD/MM/YYYY')
								: '-',
					},
					{
						title: 'NTF',
						// render: (_, record) =>
						// 	record.ntfAmount ? formatCurrency(record.ntfAmount) : '-',
						render: () => '-',
					},
					{
						title: 'OS Principal',
						// render: (_, record) =>
						// 	record.outstandingPrincipalAmount
						// 		? formatCurrency(record.outstandingPrincipalAmount)
						// 		: '-',
						render: () => '-',
					},
					{
						title: 'Sisa Limit',
						// render: (_, record) =>
						// 	record.outstandingLimitAmount
						// 		? formatCurrency(record.outstandingLimitAmount)
						// 		: '-',
						render: () => '-',
					},
					{
						title: 'Kontrak Aktif',
						// render: (_, record) => record.activeContractCount ?? '-',
						render: () => '-',
					},
					{
						title: 'Sumber Order',
						render: (_, record) => record.orderSource ?? '-',
					},
					{
						title: 'Status',
						render: (_, record) => record.status ?? '-',
					},
					{
						title: 'Sisa Masa Plafon',
						render: (_, record) => record.remainingPlafonPeriod ?? '-',
					},
					{
						title: 'Action',
						fixed: 'right',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('MonitoringPlafondAgreement', {
											spfId: record.spfId,
											onBack: () => {
												navigate('MonitoringPlafondPage', {
													customerId,
													onBack,
													search: param,
												});
											},
										});
									}}
								>
									<ArrowRightOutlined />
								</a>
							);
						},
					},
				]}
			/>
		</Fragment>
	);
};

export default MonitoringPlafondPage;
